import { FunctionComponent, useMemo } from "react";

import { useCreateCheckoutSessionForWebsiteScrapeMutation } from "@/pages/Private/redux/websiteScrape/websiteScrape.api";
import { useGetCreditsQuery } from "@/pages/Private/redux/usage/usage.api";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { Button } from "../Button/Button";
import { ButtonColor } from "../Button/types";
import { isEligibleForReducedWebsiteScrapePrice } from "./utils";

interface SelectWebsiteScrapeProductProps {
	show: boolean;
	setShow: (show: boolean) => void;
}

export const SelectWebsiteScrapeProduct: FunctionComponent<SelectWebsiteScrapeProductProps> = ({
	show,
	setShow,
}) => {
	const { user } = useAppSelector(authSelector);
	const { data: creditsData } = useGetCreditsQuery();

	const [createCheckoutSession] = useCreateCheckoutSessionForWebsiteScrapeMutation();

	const isEligibleForReducedPrice = useMemo(
		() =>
			isEligibleForReducedWebsiteScrapePrice({
				bought5Lists: user?.company?.bought5Lists,
				perMonthOrYear: creditsData?.perMonthOrYear,
			}),
		[user?.company?.bought5Lists, creditsData?.perMonthOrYear]
	);

	const handleCreateCheckoutSession = async (productId: string, priceId: string) => {
		const { sessionUrl } = await createCheckoutSession({
			priceId,
			productId,
		}).unwrap();

		window.location.href = sessionUrl;
	};

	const priceAndProducts = useMemo(() => {
		return import.meta.env.VITE_API_URL.includes("api.signalist.io")
			? [
					{
						productId: "prod_SGd7xYuMROFvLm",
						priceId: "price_1RM5rMBb9mOMNef5cjKqPM8m",
						reducedPriceId: "price_1RM6GNBb9mOMNef5kivg7GIO",
					},
					{
						productId: "prod_SGd7MzI1RGcc3a",
						priceId: "price_1RM5rJBb9mOMNef5w7dAm2QT",
					},
				]
			: [
					{
						productId: "prod_SGIs3ZHTwc1eJf",
						priceId: "price_1RLmGJBb9mOMNef5kEmcEeUF",
						reducedPriceId: "price_1RM65QBb9mOMNef5jCbjWqkd",
					},
					{
						productId: "prod_SGIsQR7SZ5Fbb7",
						priceId: "price_1RM2apBb9mOMNef53IN9cegQ",
					},
				];
	}, []);

	if (!show) {
		return <></>;
	}

	return (
		<div className="absolute z-100 left-0 top-4 w-full min-h-full flex items-center justify-center">
			<div className="flex flex-col bg-white px-6 py-10 rounded-[20px] w-[656px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center mb-6">
				<div className="flex flex-col justify-center items-center gap-2">
					<div className="text-md font-bold">
						Request Signalist to scrape <br />
						any website or mobile app
					</div>
					<div className="text-sm mb-8">(with or without login)</div>
					<div className="flex flex-row gap-2 mb-8">
						<div className="text-sm max-w-[300px] flex flex-col items-center gap-4 text-center px-4 py-3">
							<div className="flex flex-col gap-2 text-smd font-bold">Buy a single list</div>
							<p className="min-h-[96px] text-ssm">
								Get people or companies from{" "}
								<span className="font-bold">one website or mobile app</span>, e.g. if you are
								attending a conference.
							</p>
							<Button
								color={ButtonColor.ACTION}
								title={
									isEligibleForReducedPrice
										? "Buy a single list for 99 €"
										: "Buy a single list for 149 €"
								}
								onClick={() => {
									handleCreateCheckoutSession(
										priceAndProducts[0].productId,
										isEligibleForReducedPrice
											? priceAndProducts[0]?.reducedPriceId || priceAndProducts[0].priceId
											: priceAndProducts[0].priceId
									);
								}}
							/>
						</div>
						<div className="flex h-inherit w-[1px] bg-border-light">&nbsp;</div>
						<div className="text-sm max-w-[300px] flex flex-col items-center gap-4 text-center px-4 py-3">
							<div className="flex flex-col gap-2 text-smd font-bold">Buy a set of 5 lists</div>
							<p className="min-h-[96px] text-ssm">
								You can request us to scrape{" "}
								<span className="font-bold">up to 5 websites or mobile apps at any time</span> as
								long as your Signalist subscription is active.
							</p>
							<Button
								color={ButtonColor.ACTION}
								title="Buy 5 lists for 499 €"
								onClick={() => {
									handleCreateCheckoutSession(
										priceAndProducts[1].productId,
										priceAndProducts[1].priceId
									);
								}}
							/>
							{!user?.company?.bought5Lists && (
								<div className="text-xs text-text-meta">
									Additional lists can be purchased for 99 € each.
									{!isEligibleForReducedPrice &&
										" Annual and bi-annual subscribers automatically get the reduced price."}
								</div>
							)}
						</div>
					</div>
					<div className="w-auto">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title="Cancel"
							onClick={() => setShow(false)}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
